import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface SlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  widthPercent?: number; // default 30
  minWidthPx?: number; // keeps the panel readable on a small window; default 360; never wider than the window
  children: React.ReactNode;
  headerActions?: React.ReactNode;
}

const SlidePanel: React.FC<SlidePanelProps> = ({ isOpen, onClose, title, widthPercent = 30, minWidthPx, children, headerActions }) => {
  const titleId = useId();
  const panelRef = useFocusTrap<HTMLDivElement>(isOpen);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') return null;

  // Wide screens: the panel is `widthPercent` of the window, never narrower than `minWidthPx` (360 by default) and
  // never wider than the window. Under 768 px it is the whole width: 30% of a phone is a sliver nobody can read.
  const width = `min(100vw, max(${widthPercent}vw, ${minWidthPx ?? 360}px))`;

  return createPortal(
    // Closed, the whole panel is inert: it is not in the tab order and not read out, only parked off-screen.
    <div data-testid="slide-panel" className="fixed inset-0 z-[1000002] pointer-events-none" inert={!isOpen}>
      {/* Backdrop - only show when panel is open */}
      {isOpen && (
        <div
          role="presentation"
          onClick={onClose}
          className="absolute inset-0 bg-black/40 transition-opacity motion-reduce:transition-none opacity-100 pointer-events-auto"
        />
      )}
      {/* Panel: a column, so the content takes exactly what the header leaves (no height sums, and no clipping
          behind a phone's address bar). */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`absolute inset-y-0 right-0 flex w-full flex-col focus:outline-none bg-white border-l border-gray-200 transition-transform ease-out motion-reduce:transition-none pointer-events-auto md:w-[var(--panel-width)] ${
          isOpen ? 'shadow-2xl' : 'shadow-none'
        }`}
        style={{ '--panel-width': width, transform: isOpen ? 'translateX(0)' : 'translateX(100%)' } as React.CSSProperties}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="z-10 flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-3">
          <h3 id={titleId} className="min-w-0 truncate text-sm font-semibold text-secondary-900">{title}</h3>
          <div className="flex shrink-0 items-center space-x-2">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded hover:bg-secondary-100 text-secondary-500 hover:text-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-300"
              aria-label="Close"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div data-testid="panel-body" className="min-h-0 flex-1 overflow-auto p-4">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SlidePanel;


